/**
 * useDeltaSync.js
 *
 * Manages real-time email updates for the active folder via Graph delta polling.
 *
 * Behaviour:
 *  - Polls the currently selected folder every POLL_INTERVAL ms
 *  - Pauses automatically when the browser tab is hidden; resumes (with an
 *    immediate poll) when the tab becomes visible again
 *  - Applies exponential back-off on consecutive API errors, up to MAX_BACKOFF
 *  - Resets error count and restarts the timer whenever the selected folder changes
 *  - Never fires if no folder is selected or the user has not authenticated
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pollFolderDelta } from '../../../Slices/Thunk/graphMailThunks';
import { selectSelectedFolderId } from '../../../Slices/mailUiSlice';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------
const POLL_INTERVAL      = 30_000;  // 30 s — base interval between successful polls
const INITIAL_DELAY      =  8_000;  //  8 s — wait after folder selection before first poll
const BACKOFF_BASE       =  2;      // multiply interval by this on each error
const MAX_BACKOFF        = 300_000; //  5 min — cap for error back-off
// ---------------------------------------------------------------------------

export function useDeltaSync() {
  const dispatch = useDispatch();
  const folderId = useSelector(selectSelectedFolderId);

  // Use refs for everything that must not trigger re-renders
  const folderIdRef    = useRef(folderId);
  folderIdRef.current  = folderId;          // always current without re-creating callbacks

  const errorCountRef  = useRef(0);
  const timerRef       = useRef(null);
  const isVisibleRef   = useRef(!document.hidden);

  // Stable refs to the two mutually-recursive functions; avoids circular deps
  const tickRef     = useRef(null);
  const scheduleRef = useRef(null);

  // schedule(delay) — clear any pending timer, set a new one
  scheduleRef.current = (delay) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      tickRef.current?.();
    }, delay);
  };

  // tick() — perform one delta poll then reschedule
  tickRef.current = async () => {
    const fid = folderIdRef.current;

    // Skip if tab is hidden or no folder selected
    if (!fid || !isVisibleRef.current) return;

    const result = await dispatch(pollFolderDelta({ folderId: fid }));

    if (pollFolderDelta.rejected.match(result)) {
      // Exponential back-off: 30 s → 60 s → 120 s → … → 300 s
      errorCountRef.current += 1;
      const delay = Math.min(
        POLL_INTERVAL * Math.pow(BACKOFF_BASE, errorCountRef.current),
        MAX_BACKOFF,
      );
      scheduleRef.current(delay);
    } else {
      // Success — reset and schedule the next regular poll
      errorCountRef.current = 0;
      scheduleRef.current(POLL_INTERVAL);
    }
  };

  // ── Tab visibility ────────────────────────────────────────────────────────
  useEffect(() => {
    const onVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;

      if (isVisibleRef.current) {
        // Tab became visible — poll immediately (catches up on missed changes)
        clearTimeout(timerRef.current);
        tickRef.current?.();
      }
      // Tab hidden — the running timer will no-op when it fires (isVisibleRef check)
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []); // mount/unmount only

  // ── Folder change → restart polling cycle ─────────────────────────────────
  useEffect(() => {
    if (!folderId) {
      clearTimeout(timerRef.current);
      return;
    }

    // Reset back-off when user switches folders
    errorCountRef.current = 0;

    // Give thread-load a head-start before the first delta poll
    scheduleRef.current(INITIAL_DELAY);

    return () => clearTimeout(timerRef.current);
  }, [folderId]); // intentionally only folderId — refs never change identity
}
